import {
  AfterViewInit,
  Component,
  DestroyRef,
  Injector,
  NgZone,
  OnInit,
  inject
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import { AuthenticationService } from '@core/authentication/services';
import { FeatureToggleService, SecurityService } from '@mynexus/mynexus-ui-lib';
import { SharedServiceComponent } from '@shared/components/shared-service/shared-service.component';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AUTH_CONFIG, B2C_POLICIES } from '@core/config/config.service';
import { B2CPolicy, IAuthConfig } from '@env/IEnvironment';
import { AccessibilityObserverService } from './accessibility-observer.service';
import { FeatureFlag } from '@core/constants/featureFlag.enum';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionStatus,
  RedirectRequest
} from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ConfirmationService, AccessibilityObserverService]
})
export class AppComponent implements OnInit, AfterViewInit {
  private msalGuardConfig: MsalGuardConfiguration = inject(MSAL_GUARD_CONFIG);
  private authenticationService = inject(AuthenticationService);
  private injector = inject(Injector);
  private securityService = inject(SecurityService);
  private readonly msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private accessibilityObserverService = inject(AccessibilityObserverService);
  private featureToggleService = inject(FeatureToggleService);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private readonly authConfig = inject<IAuthConfig>(AUTH_CONFIG);
  private readonly b2cPolicies = inject<B2CPolicy>(B2C_POLICIES);

  public isPermissionsListLoaded = false;
  private isAccessibilityObserverFlagRequested = false;
  private redirectScheduled = false;
  private accessibilityObserverEnabled$ = new BehaviorSubject<boolean>(false);

  constructor() {
    customElements.define(
      'mnc-shared-services',
      createCustomElement(SharedServiceComponent, { injector: this.injector })
    );
  }

  public ngOnInit(): void {
    this.msalService.handleRedirectObservable({ navigateToLoginRequestUrl: false }).subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result?.account) {
          const activeAccount = this.msalService.instance.getActiveAccount();
          if (!activeAccount || activeAccount?.homeAccountId !== result.account?.homeAccountId) {
            this.msalService.instance.setActiveAccount(result.account);
          }
        }
      },
      error: err => {
        if (this.isForgotPasswordError(err)) {
          this.scheduleRedirect(this.buildPolicyRequest(this.b2cPolicies.names.forgotPassword));
        } else if (this.isUserCancelledError(err)) {
          this.scheduleRedirect(this.buildPolicyRequest(this.b2cPolicies.names.signUpSignIn));
        } else {
          console.error('[MSAL] Redirect handling error:', err);
        }
      }
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaims;

        /**
         * If the user is returning from the reset-password flow the token was issued by the
         * PasswordReset policy and is not valid for our APIs. Do NOT make it the active account;
         * force a fresh sign-in with the new password under the SignUpSignIn policy instead.
         */
        if (
          idtoken.acr === this.b2cPolicies.names.forgotPassword ||
          idtoken.tfp === this.b2cPolicies.names.forgotPassword
        ) {
          this.scheduleRedirect(this.buildPolicyRequest(this.b2cPolicies.names.signUpSignIn));
          return;
        }

        if (
          idtoken.tfp === this.b2cPolicies.names.signUpSignIn ||
          idtoken.acr === this.b2cPolicies.names.signUpSignIn
        ) {
          this.msalService.instance.setActiveAccount(payload.account);

          if (!this.isPermissionsListLoaded) {
            this.initializePermissions();
          }
        }
      });

    // The "Forgot your password?" link returns AADB2C90118. In msal-browser v5 a failed redirect
    // surfaces as ACQUIRE_TOKEN_FAILURE (there is no LOGIN_FAILURE event) and also rejects the
    // handleRedirectObservable promise handled above; scheduleRedirect() dedupes the two paths.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (this.isForgotPasswordError(result.error)) {
          this.scheduleRedirect(this.buildPolicyRequest(this.b2cPolicies.names.forgotPassword));
        } else if (this.isUserCancelledError(result.error)) {
          this.scheduleRedirect(this.buildPolicyRequest(this.b2cPolicies.names.signUpSignIn));
        }
      });
  }

  private isForgotPasswordError(error: unknown): boolean {
    return error instanceof Error && error.message.indexOf('AADB2C90118') > -1;
  }

  private isUserCancelledError(error: unknown): boolean {
    return error instanceof Error && error.message.indexOf('AADB2C90091') > -1;
  }

  /** Builds a redirect request targeting a specific B2C user-flow (policy). */
  private buildPolicyRequest(policyName: string): RedirectRequest {
    return {
      authority: `${this.b2cPolicies.authorityBaseUrl}/${policyName}`,
      scopes: this.authConfig.angularConfig.consentScopes
    };
  }

  /**
   * Defers a redirect until MSAL reports no interaction in progress. Triggering loginRedirect()
   * synchronously from inside a redirect event throws `interaction_in_progress`, which leaves a
   * stuck status key in session storage and freezes every subsequent attempt (the dead loop).
   * Gating on InteractionStatus.None — and a one-shot flag — avoids that collision.
   */
  private scheduleRedirect(request: RedirectRequest): void {
    if (this.redirectScheduled) {
      return;
    }
    this.redirectScheduled = true;

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.login(request));
  }

  ngAfterViewInit(): void {
    combineLatest([this.ngZone.onStable.pipe(map(() => true)), this.accessibilityObserverEnabled$])
      .pipe(
        filter(([, isEnabled]) => isEnabled === true),
        take(1)
      )
      .subscribe(() => this.accessibilityObserverService.start());
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    if (this.msalService.instance.getActiveAccount() && !this.isAccessibilityObserverFlagRequested) {
      this.isAccessibilityObserverFlagRequested = true;
      this.featureToggleService
        .getFeatureAccess(FeatureFlag.ExternalWCAGAccessibilityObserver)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(enabled => this.accessibilityObserverEnabled$.next(enabled));
    }
  }

  login(userFlowRequest?: RedirectRequest) {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect(userFlowRequest);
    }
  }

  private initializePermissions(): void {
    this.authenticationService.loadUserPermissions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: permissions => {
          this.securityService.setUserPermissions(Object.values(permissions).flat());
          this.securityService.setAllowEmptyPermission(false);
          this.isPermissionsListLoaded = true;
        },
        error: err => console.error('Failed to load permissions:', err)
      });
  }
}
